/**
 * @fileoverview API service for Email Marketing feature.
 * All calls go through the Deals_Backend.
 */

import { BE_URLS } from "@/constants/config.js";
import axios from "@/api/axios/axios_core_deal.js";

const DEAL_HOST = BE_URLS.BACKEND_DEAL_SERVER_URL;

// ==========================================
// Template Generation (via Deals_Backend AI)
// ==========================================

/**
 * Send uploaded image URLs + business info to AI to generate an HTML email template.
 * @param {Array} images - Array of uploaded image URLs
 * @param {Object} businessInfo - { name, tagline }
 * @param {string} templateType - promotional | newsletter | announcement | welcome
 * @returns {Promise} { html, subject }
 */
export async function generateEmailTemplate(images, businessInfo, templateType, ctaUrl) {
  const response = await axios.post(`${DEAL_HOST}/email-marketing/generate-template`, {
    images, businessInfo, templateType, ctaUrl
  });
  return response.data;
}

/**
 * Upload an image for use in email templates.
 * Uses the existing /upload endpoint.
 * @param {FormData} formData - Multipart form data with 'file' field
 * @returns {Promise} Array of { url, filename }
 */
export async function uploadImage(formData) {
  const response = await axios.post(`${DEAL_HOST}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

// ==========================================
// Campaign Sending (via Deals_Backend)
// ==========================================

/**
 * Send an email campaign to a list of recipients.
 * @param {string} templateId - Saved template ID
 * @param {string} templateHtml - HTML content with placeholders
 * @param {string} subject - Email subject line
 * @param {string} senderName - Sender display name
 * @param {string} senderEmail - Sender email address
 * @param {Array} recipients - Array of { name, email }
 * @returns {Promise} { campaignId, sent, failed, total }
 */
export async function sendCampaign(params) {
  const response = await axios.post(`${DEAL_HOST}/email-marketing/send-campaign`, params);
  return response.data;
}

/**
 * Get stats for a sent campaign.
 * @param {string} campaignId
 * @returns {Promise} { sent, failed, opened, clicked, total }
 */
export async function getCampaignStats(campaignId) {
  const response = await axios.get(`${DEAL_HOST}/email-marketing/campaign-stats/${campaignId}`);
  return response.data;
}

/**
 * Get all campaigns for the current merchant.
 * @returns {Promise} Array of campaigns
 */
export async function getCampaigns() {
  const response = await axios.get(`${DEAL_HOST}/email-marketing/campaigns`);
  return response.data;
}

/**
 * Delete a campaign.
 * @param {string} campaignId
 */
export async function deleteCampaign(campaignId) {
  const response = await axios.delete(`${DEAL_HOST}/email-marketing/campaigns/${campaignId}`);
  return response.data;
}

// ==========================================
// Template CRUD (via Deals_Backend)
// ==========================================

/**
 * Get all saved email templates.
 * @returns {Promise} Array of templates
 */
export async function getTemplates() {
  const response = await axios.get(`${DEAL_HOST}/email-templates`);
  return response.data;
}

/**
 * Create a new email template.
 * @param {Object} template - { name, html, subject, thumbnailUrl, images }
 * @returns {Promise} Created template
 */
export async function createTemplate(template) {
  const response = await axios.post(`${DEAL_HOST}/email-templates`, template);
  return response.data;
}

/**
 * Update an email template.
 * @param {string} id - Template ID
 * @param {Object} data - Fields to update
 * @returns {Promise} Updated template
 */
export async function updateTemplate(id, data) {
  const response = await axios.put(`${DEAL_HOST}/email-templates/${id}`, data);
  return response.data;
}

/**
 * Delete an email template.
 * @param {string} id - Template ID
 * @returns {Promise}
 */
export async function deleteTemplate(id) {
  const response = await axios.delete(`${DEAL_HOST}/email-templates/${id}`);
  return response.data;
}

// ==========================================
// Email List CRUD (via Deals_Backend)
// ==========================================

/**
 * Get all email lists.
 * @returns {Promise} Array of email lists
 */
export async function getEmailLists() {
  const response = await axios.get(`${DEAL_HOST}/email-lists`);
  return response.data;
}

/**
 * Create a new email list.
 * @param {Object} list - { name, recipients: [{ name, email }] }
 * @returns {Promise} Created list
 */
export async function createEmailList(list) {
  const response = await axios.post(`${DEAL_HOST}/email-lists`, list);
  return response.data;
}

/**
 * Update an email list.
 * @param {string} id - List ID
 * @param {Object} data - Fields to update
 * @returns {Promise} Updated list
 */
export async function updateEmailList(id, data) {
  const response = await axios.put(`${DEAL_HOST}/email-lists/${id}`, data);
  return response.data;
}

/**
 * Delete an email list.
 * @param {string} id - List ID
 * @returns {Promise}
 */
export async function deleteEmailList(id) {
  const response = await axios.delete(`${DEAL_HOST}/email-lists/${id}`);
  return response.data;
}

// ==========================================
// Unsubscribe List (via Deals_Backend)
// ==========================================

/**
 * Get the merchant's unsubscribe list.
 * @returns {Promise} Array of { email, campaignId, source, unsubscribedAt }
 */
export async function getUnsubscribeList() {
  const response = await axios.get(`${DEAL_HOST}/email-marketing/unsubscribes`);
  return response.data;
}
